/**
 * The organisation switcher at the top of the console's sidebar, under the wordmark.
 *
 * It names the organisation the console is showing, lists the others this account belongs
 * to, and offers to create a new one, which goes to the same create screen that onboarding uses.
 *
 * Switching resets every organisation-scoped query (see `resetTenantQueries`) before the
 * session reloads, so the console never shows one organisation's data under another's name.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Check, ChevronsUpDown, Plus } from 'lucide-react'
import { authClient } from '@web/auth'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@layerit/ui/components/ui/dropdown-menu'
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from '@layerit/ui/components/ui/sidebar'
import { useOrganizations } from '@web/hooks/organizations'
import { followLink } from '@web/lib/navigation'
import { cn } from '@layerit/ui/lib/utils'
import { resetTenantQueries } from '@web/query'
import { ROUTES } from '@web/router'
import { initialsOf } from '@web/screens/choose-organisation'

/**
 * An organisation's initials on a tile.
 *
 * Built from theme tokens, not the chooser's pastel tints. Those tints have no dark-theme
 * variant, so on the dark sidebar they read as washed-out glyphs on a glaring tile. The
 * current organisation gets the solid primary fill, so its tile alone marks which one you are in.
 */
/**
 * Makes `organizationId` the session's active organisation on the server, then brings the
 * console along with it.
 *
 * `setActive` is the handshake: Better Auth writes `activeOrganizationId` onto the session row,
 * and every API request resolves its tenant from that. Nothing on the client changes until the
 * server has said yes. After that, organisation-scoped queries are reset (see
 * `resetTenantQueries`), the session reloads and the console returns to Get started. The sidebar
 * switcher and the global search share this hook so the two cannot drift apart.
 */
export function useSwitchOrganisation(reload: () => Promise<void>, navigate: (to: string) => void) {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: async (organizationId: string) => {
			const result = await authClient.organization.setActive({ organizationId })
			if (result.error) throw new Error(result.error.message ?? 'That organisation could not be opened.')
		},
		onSuccess: async () => {
			await resetTenantQueries(queryClient)
			await reload()
			navigate(ROUTES.getStarted)
		},
	})
}

export function OrganisationMark({ name, current, className }: { name: string; current: boolean; className?: string }) {
	return (
		<span
			aria-hidden
			className={cn(
				'grid size-8 shrink-0 place-items-center rounded-md text-xs font-semibold tracking-wide antialiased',
				current ? 'bg-primary text-primary-foreground' : 'border border-border bg-card text-foreground',
				className,
			)}
		>
			{initialsOf(name)}
		</span>
	)
}

export function OrganisationSwitcher({
	activeId,
	reload,
	navigate,
}: {
	/** The member's `tenantId`, which the API resolves from the session's active organisation. */
	activeId: string
	reload: () => Promise<void>
	navigate: (to: string) => void
}) {
	const { isMobile } = useSidebar()
	const organizations = useOrganizations()

	const roster = organizations.data ?? []
	const active = roster.find((organization) => organization.id === activeId)

	const switchTo = useSwitchOrganisation(reload, navigate)

	const createHref = ROUTES.newOrganisation
	const label = active?.name ?? (organizations.isPending ? 'Loading…' : 'Organisation')

	return (
		<SidebarMenu>
			<SidebarMenuItem>
				<DropdownMenu>
					<DropdownMenuTrigger
						render={
							<SidebarMenuButton
								size="lg"
								tooltip={label}
								aria-label={`Organisation: ${label}. Switch or create an organisation`}
								// The sidebar's own hover colour sits a hair from its background in the
								// light theme. This is the one control up here, so it gets a framed card that
								// visibly lifts on hover and while its menu is open.
								className="border border-border bg-card shadow-xs transition-[background-color,border-color,box-shadow] hover:border-ink-3/40 hover:bg-card hover:shadow-sm data-popup-open:border-ink-3/40 data-popup-open:bg-card data-popup-open:shadow-sm group-data-[collapsible=icon]:border-transparent group-data-[collapsible=icon]:bg-transparent group-data-[collapsible=icon]:shadow-none"
							/>
						}
					>
						{active ? (
							<OrganisationMark name={active.name} current />
						) : (
							<span aria-hidden className="size-8 shrink-0 rounded-md bg-muted" />
						)}
						<span className="flex min-w-0 flex-1 flex-col text-left leading-tight">
							<span className="truncate text-sm font-medium">{label}</span>
							<span className="truncate text-xs text-muted-foreground">Current organisation</span>
						</span>
						<ChevronsUpDown className="ml-auto text-muted-foreground" />
					</DropdownMenuTrigger>

					<DropdownMenuContent side={isMobile ? 'bottom' : 'right'} align="start" className="w-auto min-w-60">
						<DropdownMenuGroup>
							<DropdownMenuLabel className="text-xs text-muted-foreground">Organisations</DropdownMenuLabel>
							{roster.map((organization) => {
								const current = organization.id === activeId
								return (
									<DropdownMenuItem
										key={organization.id}
										disabled={switchTo.isPending}
										aria-current={current ? 'true' : undefined}
										onClick={() => {
											if (!current) switchTo.mutate(organization.id)
										}}
										className={cn('gap-2.5 py-1.5 focus:bg-border/70', current && 'bg-muted font-medium')}
									>
										<OrganisationMark name={organization.name} current={current} className="size-7 text-[11px]" />
										<span className="min-w-0 flex-1 truncate">{organization.name}</span>
										{current && (
											<span className="flex items-center gap-1 text-xs text-muted-foreground">
												Current
												<Check className="size-3.5 text-foreground" />
											</span>
										)}
									</DropdownMenuItem>
								)
							})}
							{organizations.isError && (
								<DropdownMenuLabel className="text-xs font-normal text-fault">
									Your organisations could not be loaded.
								</DropdownMenuLabel>
							)}
							{switchTo.isError && (
								<DropdownMenuLabel className="text-xs font-normal text-fault">
									{switchTo.error instanceof Error ? switchTo.error.message : 'That organisation could not be opened.'}
								</DropdownMenuLabel>
							)}
						</DropdownMenuGroup>
						<DropdownMenuSeparator />
						<DropdownMenuItem
							className="gap-2.5 py-1.5 focus:bg-border/70"
							render={<a href={createHref} onClick={(event) => followLink(event, createHref, navigate)} />}
						>
							<span className="grid size-7 place-items-center rounded-md border border-dashed border-ink-3/50">
								<Plus className="size-3.5" />
							</span>
							Create organisation
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</SidebarMenuItem>
		</SidebarMenu>
	)
}
